// Route Code Edited 4/27/2025
/**
 * Route Back-Navigation Flicker Fix — Part 2 of 2 (deferred)
 *
 * On back-navigation from checkout, Route finds its product in the cart,
 * removes it, then calls window.location.reload() to clear stale cart UI.
 * This script removes the Route product first via the Cart API, updates
 * the cart UI via cart:change, then re-injects Route's script so it
 * initializes against a clean cart and its reload branch never fires.
 *
 * window.location.reload is suppressed for the duration of the removal
 * window to prevent a bfcache-restored Route instance from reloading the
 * page after detecting its own product was removed.
 *
 * Depends on window.__routeIntercept and window.__routeBlocked set by the
 * inline script in theme.liquid.
 */
(function () {

  const FLAG_KEY = 'route_pending_removal';
  const ROUTE_CACHE_KEY = 'route-product-removed-by-route';
  const ROUTE_CACHE_TTL_24_HOURS = 24 * 60 * 60 * 1000;

  const setFlag = () => sessionStorage.setItem(FLAG_KEY, '1');
  const clearFlag = () => sessionStorage.removeItem(FLAG_KEY);

  function setRouteRemovedCache() {
    localStorage.setItem(ROUTE_CACHE_KEY, JSON.stringify({
      value: { routeProductRemovedByRoute: true },
      ttl: Date.now() + ROUTE_CACHE_TTL_24_HOURS
    }));
  }

  function clearRouteRemovedCache() {
    localStorage.removeItem(ROUTE_CACHE_KEY);
  }

  function attachCheckoutListener() {
    document.addEventListener('click', e => {
      if (e.target.closest('[data-route-cloned-button]')) setFlag();
    }, true);
  }

  function waitForBlockedScripts() {
    if (window.__routeBlocked?.length) return Promise.resolve();
    return new Promise(resolve => {
      const start = Date.now();
      const interval = setInterval(() => {
        if (window.__routeBlocked?.length) {
          clearInterval(interval);
          resolve();
        } else if (Date.now() - start >= 1000) {
          clearInterval(interval);
          resolve();
        }
      }, 20);
    });
  }

  async function removeRouteAndRestoreScript() {
    const intercept = window.__routeIntercept;
    const originalReload = window.location.reload.bind(window.location);

    window.location.reload = function () {};

    function restoreReload() {
      window.location.reload = originalReload;
    }

    function abort() {
      intercept.restore();
      restoreReload();
      window.__routeBlocked.forEach(src => intercept.inject(src));
    }

    await waitForBlockedScripts();

    if (!window.__routeBlocked?.length) {
      intercept.restore();
      restoreReload();
      return;
    }

    clearFlag();
    setRouteRemovedCache();

    let cartData;
    try {
      const res = await fetch('/cart.js');
      if (!res.ok) throw new Error();
      cartData = await res.json();
    } catch (e) {
      clearRouteRemovedCache();
      abort();
      return;
    }

    const routeItems = cartData.items.filter(item => item.vendor === 'Route');

    if (routeItems.length === 0) {
      abort();
      return;
    }

    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
      bubbles: true,
      detail: { sections: sectionsToBundle }
    }));

    let removalResponse;
    for (const item of routeItems) {
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: item.key, quantity: 0, sections: sectionsToBundle.join(',') })
        });
        if (!res.ok) throw new Error();
        removalResponse = await res.json();
      } catch (e) {
        clearRouteRemovedCache();
        abort();
        return;
      }
    }

    document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
      bubbles: true,
      detail: { baseEvent: 'line-item:change', cart: removalResponse }
    }));

    intercept.restore();
    restoreReload();
    window.__routeBlocked.forEach(src => intercept.inject(src));
  }

  attachCheckoutListener();
  if (!sessionStorage.getItem(FLAG_KEY)) return;
  removeRouteAndRestoreScript();
})();
// End Route Code